// client/customer/src/pages/Reserve/by-service/SelectStore.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Store } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  fetchStoresForServiceReserve,
  type ReserveStore,
} from '../../../utils/reservation/by-service/fetchStoresForServiceReserve';
import { useReserveContext } from '../../../contexts/useReserveContext';
import { applySelectedStore } from '../storeSelection';

import ReserveStepHeader from '../ReserveStepHeader';
import { useI18n } from '../../../i18n';

const SelectStoreByService: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const {
    storeId,
    setSkippedStoreSelectionByService,
    setStoreId,
    setStoreName,
    setSelectedServices,
    setSelectedDate,
    setSelectedStaffId,
    setSelectedStaffName,
    setSelectedStartTime,
    setIsFreeReservation,
  } = useReserveContext();

  const [stores, setStores] = useState<ReserveStore[]>([]);
  const [loading, setLoading] = useState(true);
  const storeSelectionActions = useMemo(
    () => ({
      setStoreId,
      setStoreName,
      setSelectedServices,
      setSelectedDate,
      setSelectedStaffId,
      setSelectedStaffName,
      setSelectedStartTime,
      setIsFreeReservation,
    }),
    [
      setStoreId,
      setStoreName,
      setSelectedServices,
      setSelectedDate,
      setSelectedStaffId,
      setSelectedStaffName,
      setSelectedStartTime,
      setIsFreeReservation,
    ],
  );

  useEffect(() => {
    const load = async () => {
      const data = await fetchStoresForServiceReserve();
      setStores(data);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (loading) return;
    setSkippedStoreSelectionByService(stores.length === 1);
  }, [loading, setSkippedStoreSelectionByService, stores.length]);

  useEffect(() => {
    if (loading || stores.length !== 1) return;
    applySelectedStore(storeSelectionActions, stores[0]);
    navigate('/reserve/by-service/service', { replace: true });
  }, [loading, navigate, storeSelectionActions, stores]);

  const onSelect = (s: ReserveStore) => {
    setSkippedStoreSelectionByService(false);
    applySelectedStore(storeSelectionActions, s);
    navigate('/reserve/by-service/service');
  };

  if (!loading && stores.length === 1) {
    return <div className="py-12 text-center text-sm text-slate-400">{t('store.openingFlow')}</div>;
  }

  return (
    <div className="space-y-4">
      <ReserveStepHeader
        title={t('store.chooseTitle')}
        onBack={() => navigate('/reserve')}
        step={1}
        totalSteps={5}
      />

      <div className="rounded-[28px] border border-[#d7efee] bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.96))] p-4 shadow-[0_14px_30px_rgba(10,186,181,0.08)]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5a9d99]">{t('store.eyebrow')}</div>
        <div className="mt-1 text-sm font-semibold text-slate-900">{t('store.selectLocation')}</div>
        <div className="mt-1 text-sm text-slate-500">
          {t('store.selectLocationHelp')}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">{t('store.loading')}</p>
      ) : (
        <div className="space-y-3">
          {stores.map((s) => {
            const isActive = storeId === s.id;

            return (
              <button
                key={s.id}
                onClick={() => onSelect(s)}
                className={[
                  'w-full rounded-[28px] border px-4 py-4 text-left shadow-sm transition',
                  isActive
                    ? 'border-[#87dfdb] bg-[linear-gradient(180deg,rgba(223,248,246,0.96),rgba(255,255,255,1))] shadow-[0_14px_28px_rgba(10,186,181,0.12)]'
                    : 'border-slate-200 bg-white hover:border-[#c3ece9] hover:bg-[#fbfefe]',
                ].join(' ')}
              >
                <div className="flex items-center gap-4">
                  <div className={`rounded-3xl p-3 ${isActive ? 'bg-[#0ABAB5] text-white' : 'bg-slate-100 text-slate-700'}`}>
                    <Store className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold text-slate-900">{s.name}</div>
                  </div>
                  <ChevronRight className={`h-5 w-5 ${isActive ? 'text-[#0ABAB5]' : 'text-slate-300'}`} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SelectStoreByService;
